import { useDocumentDestroy } from "gen/endpoints/documents/documents";
import { DocumentReadData } from "gen/model/documentReadData";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import React, { useCallback, useMemo } from "react";
import { FileList } from "shared/components/file/FileList";
import { FileListItem } from "shared/components/file/FileListItem";
import { FileState } from "shared/components/file/FileState";

export const DocumentList: React.FC<{
  documents: DocumentReadData[];
  onDocumentDeleted?: (document: DocumentReadData) => void;
}> = ({ documents, onDocumentDeleted }) => {
  const { mutateAsync: destroyDocument } = useDocumentDestroy();
  const onClickRemove = useCallback(
    async (document: DocumentReadData) => {
      await destroyDocument({ id: document.id });
      onDocumentDeleted?.(document);
    },
    [destroyDocument, onDocumentDeleted],
  );

  const documentListItems = useMemo(
    () =>
      documents.map((item) => (
        <DocumentListItem
          key={item.id}
          item={item}
          onClickRemove={onClickRemove}
        />
      )),
    [documents, onClickRemove],
  );

  return <FileList>{documentListItems}</FileList>;
};

const DocumentListItem: React.FC<{
  item: DocumentReadData;
  onClickRemove: (item: DocumentReadData) => void;
}> = ({ item, onClickRemove: onClickRemoveProp }) => {
  const onClickRemove = useCallback(() => {
    onClickRemoveProp(item);
  }, [onClickRemoveProp, item]);
  return (
    <FileListItem
      key={item.id}
      fileName={item.filename}
      fileSize={item.size}
      state={documentStateToFileState[item.state]}
      onClickRemove={onClickRemove}
    />
  );
};

const documentStateToFileState: Record<
  DocumentStateMachineFieldsEnum,
  FileState
> = {
  [DocumentStateMachineFieldsEnum.no_upload]: "idle",
  [DocumentStateMachineFieldsEnum.upload_succeeded]: "uploading_complete",
  [DocumentStateMachineFieldsEnum.upload_failed]: "uploading_failed",
  [DocumentStateMachineFieldsEnum.queued_for_ingestion]: "queued",
  [DocumentStateMachineFieldsEnum.ingesting]: "processing",
  [DocumentStateMachineFieldsEnum.failed_ingestion]: "processing_failed",
  [DocumentStateMachineFieldsEnum.successful_ingestion]: "processing_complete",
  [DocumentStateMachineFieldsEnum.deleted]: "deleted",
  [DocumentStateMachineFieldsEnum.out_of_date]: "processing_complete",
};
